import { Injectable } from '@angular/core'
import { containsFormula, Formula, FORMULA_CLASS, formulaFallbackText, formulaToTex, readFormulaAttributes } from '@monorepo/utils'

type Katex = typeof import('./katex-with-mhchem').default

/** `html` is enough inside the editor; `htmlAndMathml` adds MathML for screen readers. */
export type FormulaOutput = 'html' | 'htmlAndMathml'

export type FormulaCheck = { html: string; error?: undefined } | { html?: undefined; error: string }

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * Draws chemical and mathematical formulas with KaTeX and mhchem. KaTeX is loaded the first time
 * a formula is needed; until then formulas show the plain Unicode text saved inside them.
 */
@Injectable({
  providedIn: 'root',
})
export class FormulaRenderService {
  private katex: Katex | undefined
  private loading: Promise<void> | undefined

  get isLoaded(): boolean {
    return !!this.katex
  }

  load(): Promise<void> {
    this.loading ??= import('./katex-with-mhchem')
      .then((module) => {
        this.katex = module.default
      })
      .catch((error) => {
        this.loading = undefined
        throw error
      })
    return this.loading
  }

  /** Renders a formula, or says why it cannot be read. Only works once load() has finished. */
  check(formula: Formula, output: FormulaOutput = 'htmlAndMathml'): FormulaCheck {
    if (!this.katex) {
      return { error: 'Formulas are still loading' }
    }
    try {
      const html = this.katex.renderToString(formulaToTex(formula), {
        displayMode: formula.display,
        output,
        strict: 'ignore',
        throwOnError: true,
      })
      return { html }
    } catch (error) {
      // mhchem reports problems by throwing an array of strings instead of an Error
      const message = Array.isArray(error)
        ? String(error[error.length - 1])
        : error instanceof Error
          ? error.message
          : String(error)
      return { error: message }
    }
  }

  /** Renders a formula; one that cannot be read shows its plain-text version. */
  render(formula: Formula, output: FormulaOutput = 'htmlAndMathml'): string {
    return this.check(formula, output).html ?? escapeHtml(formulaFallbackText(formula))
  }

  /** Draws every saved formula in the HTML of a text element. Returns the HTML unchanged until load() has finished. */
  renderFormulasInHtml(html: string): string {
    if (!this.katex || !containsFormula(html)) {
      return html
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    parsed.body.querySelectorAll<HTMLElement>(`span.${FORMULA_CLASS}`).forEach((element) => {
      element.innerHTML = this.render(readFormulaAttributes((name) => element.getAttribute(name)))
    })
    return parsed.body.innerHTML
  }
}
